import React, { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { RootState } from '@@/store';
import { useLoginMutation } from '@@/services/auth';
import { Roles } from '@@/constants/user';
import StandardInput from '@@/components/Inputs/StandardInput';

const schema = yup
  .object({
    username: yup
      .string()
      .email('Veuillez entrer un identifiant valide')
      .required('Veuillez entrer votre identifiant'),
    password: yup.string().required('Veuillez entrer votre mot de passe'),
    role: yup.string().oneOf(Object.values(Roles)),
  })
  .required();

const SigninPage: React.FC = () => {
  const auth = useSelector((state: RootState) => state.auth);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Unpatient.SigninInput>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const navigate = useNavigate();
  const [login, { isSuccess }] = useLoginMutation();

  const loginHandler = useCallback(
    async (data: Unpatient.SigninInput) => {
      return login({ ...data, role: Roles.PRACTITIONER });
    },
    [login],
  );

  useEffect(() => {
    if (isSuccess) {
      reset();
      navigate('/patients');
    }
  }, [isSuccess, navigate, reset]);

  useEffect(() => {
    if (auth.accessToken) {
      navigate('/patients');
    }
  }, [auth.accessToken, navigate]);

  return (
    <div className="flex flex-col justify-center items-center h-screen bg-cyan-400">
      <img src="/logo-black.png" className="w-40 mb-4 sm:mb-8" />
      <div className="p-10 w-[300px] rounded-xl flex flex-col bg-white opacity-90 sm:w-[600px]">
        <form autoComplete="off">
          <p className="font-main text-2xl text-main text-center mb-12">
            Accès Professionel de santé
          </p>
          <div className="flex flex-col space-y-10 sm:space-y-12">
            <StandardInput
              register={register}
              id="username"
              label="Identifiant *"
              type="text"
              placeholder="Votre identifiant"
              error={errors.username}
            />
            <StandardInput
              register={register}
              id="password"
              label="Mot de passe *"
              type="password"
              placeholder="Votre mot de passe"
              error={errors.password}
            />
          </div>
        </form>
        <button
          type="button"
          className="font-main text-base bg-cyan-400 p-3 mt-10"
          onClick={handleSubmit(loginHandler)}
          disabled={isSubmitting}
        >
          <span className="text-white">Se connecter</span>
        </button>
        <Link
          to="/reset-password"
          className="mt-2 text-center font-main text-main text-sm underline italic"
        >
          Mot de passe oublié ?
        </Link>
        <p className="mt-6 font-main text-sm">
          Avant d'utiliser l'application UNPATIENT, consultez&nbsp;
          <a
            href="https://s3.eu-west-3.amazonaws.com/public.unpatient.me/cgu-ps.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 font-main underline italic"
          >
            les conditions générales d'utilisation
          </a>
        </p>
      </div>
    </div>
  );
};

export default SigninPage;
